package com.crowpay.views.components.project.work.payApps

import com.crowpay.DisplayableState
import com.crowpay.LineItemInterface
import com.crowpay.LineItemState
import com.crowpay.PayAppItem
import com.crowpay.PayApplication
import com.crowpay.isComplete
import com.crowpay.utils.AdjustedLineItem
import com.crowpay.views.screens.common.HasProjectData
import com.crowpay.views.theming.AppColors
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.shared
import kotlinx.datetime.Instant
import kotlin.math.roundToLong

interface DirectlyDisplayableState : DisplayableState {
    val color : Color
    override val displayColor: String get() = color.toAlphalessWeb()
}

enum class PayAppStatus(override val displayName: String, override val color: Color) : DirectlyDisplayableState {
    Draft("Draft", AppColors.grey.main),
    Pending("Submitted", AppColors.primary.main),
    Review("Open - Partial", AppColors.accent.main),
    Partial("Closed - Partial", AppColors.accent.main),
    Denied("Closed - Unpaid", AppColors.red.main),
    Approved("Paid", AppColors.secondary.main),
    Voided("Voided", AppColors.red.main)
    ;

    override val priority: Int get() = 0
}

val PayAppItem.status: PayAppStatus get() = when {
    voided != null -> PayAppStatus.Voided
    denied != null -> PayAppStatus.Denied
    approved != null -> PayAppStatus.Approved
    submitted == null -> PayAppStatus.Draft
    else -> PayAppStatus.Pending
}


enum class PayAppItemStatus(override val displayName: String, override val color: Color) : DirectlyDisplayableState {
    Pending("Submitted", AppColors.primary.main),
    Denied("Waited", AppColors.red.main),
    Approved("Paid", AppColors.secondary.main),
    Voided("Voided", AppColors.red.main)
    ;

    override val priority: Int get() = 0
}

val PayAppItem.individualStatus: PayAppItemStatus get() = when {
    voided != null -> PayAppItemStatus.Voided
    denied != null -> PayAppItemStatus.Denied
    approved != null -> PayAppItemStatus.Approved
    else -> PayAppItemStatus.Pending
}


fun Set<PayAppStatus>.overallStatus() = when {
    isEmpty() -> PayAppStatus.Draft
    size == 1 -> single()
    any { it == PayAppStatus.Pending } -> PayAppStatus.Review
    none { it == PayAppStatus.Pending || it == PayAppStatus.Draft } -> PayAppStatus.Partial
    else -> firstOrNull() ?: PayAppStatus.Draft
}

// Combines all the payment info for a line item, but also "rewinds" the line item and modifications to a certain instant
data class LineItemWithPaymentInfo(
    val lineItem: AdjustedLineItem,
    val payApp: PayAppItem?,
    val activePayApps: List<PayAppItem>,
    private val cutoff: Instant
) : LineItemInterface by lineItem {
    private val rewound = activePayApps.map { payApp ->
        payApp.copy(
            submitted = payApp.submitted?.takeUnless { it > cutoff },
            voided = payApp.voided?.takeUnless { it.timestamp > cutoff },
            approved = payApp.approved?.takeUnless { it.timestamp > cutoff },
            denied = payApp.denied?.takeUnless { it.timestamp > cutoff }
        )
    }

    val previousPayments =
        if (lineItem.state.isComplete()) lineItem.price
        else rewound.filter { it.approved != null }.sumOf { it.amount }

    val pending =
        if (lineItem.state == LineItemState.RequestingReview) lineItem.price - previousPayments
        else rewound.filter { it.pending }.sumOf { it.amount }

    val thisPayment = payApp?.amount

    val workCompleted =
        if (lineItem.state.isComplete()) lineItem.price
        else previousPayments + pending + (thisPayment ?: 0)


    val percentComplete =
        if (price > 0) workCompleted.times(100)/price
        else 0

    fun retentionToDate(retention: Float) = (workCompleted * retention).roundToLong()
    fun contractorPaymentsToDate(retention: Float) = workCompleted - retentionToDate(retention)
}

fun List<LineItemWithPaymentInfo>.percentComplete(): Long {
    val total = sumOf { it.price }
    return if (total > 0) sumOf { it.workCompleted }.times(100) / total
    else 0
}
fun List<LineItemWithPaymentInfo>.retentionToDate(retention: Float): Long = sumOf { it.retentionToDate(retention) }

fun ReactiveContext.getPaymentData(
    data: HasProjectData,
    payApp: PayApplication
): List<LineItemWithPaymentInfo> {
    val allItems = data.payAppItems()
    val thisPayAppItems = allItems.filter { it.payApp == payApp._id }
    val firstReview = thisPayAppItems
        .minOfOrNull { it.reviewedTimestamp ?: Instant.DISTANT_FUTURE }
        ?: Instant.DISTANT_FUTURE
    val project = data.project.once()

    return data.lineItems()
        .mapNotNull { line ->
            val associated = thisPayAppItems.find { it.lineItem == line._id }
            val cutoff = associated?.submitted ?: firstReview
            line
                .excludeChangesAfter(
                    cutoff,
                    data.lineItemCompletionMessages().filter { it.lineItem == line._id },
                    project.preSubstantialCompletion
                )        // "Rewinds" the line item to the provided timestamp. If the line item didn't exist at that point then returns null
                ?.let { rewound ->
                    LineItemWithPaymentInfo(
                        lineItem = rewound,
                        payApp = associated,
                        activePayApps = allItems.filter { payItem ->
                            payItem._id != associated?._id && payItem.lineItem == rewound._id && payItem.voided == null &&
                                    ((associated != null && associated.submitted == null) || payItem.submitted.let { it != null && it < cutoff })
                        },
                        cutoff
                    )
                }
        }
        .sortedBy { it.lineItem.wraps }
}